/**
 * Oddit size guide drawer — drill-down navigation.
 *
 * The drawer body holds a root "list" view plus one full-panel detail page per
 * accordion block. Tapping a list row opens its page (slides in from the right);
 * a back row returns to the list. Closing the modal resets back to the list.
 *
 * Vanilla, no-build, delegated so it survives theme-editor re-renders. Native
 * popover Escape / backdrop dismiss is left untouched (closes the whole modal).
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '.oddit-size-guide-drawer__view--root';

  function getViews(el) {
    return el.closest('[data-size-guide-views]');
  }

  function getScroller(views) {
    return views.closest('.oddit-size-guide-drawer');
  }

  function resetScroll(views) {
    var scroller = getScroller(views);
    if (scroller) scroller.scrollTop = 0;
  }

  function showRoot(views, animateReturn) {
    var root = views.querySelector(ROOT_SELECTOR);
    views
      .querySelectorAll('.oddit-size-guide-drawer__view--detail')
      .forEach(function (page) {
        page.hidden = true;
      });
    if (!root) return;
    root.hidden = false;
    if (animateReturn) {
      root.classList.add('is-returning');
      root.addEventListener(
        'animationend',
        function handle() {
          root.classList.remove('is-returning');
          root.removeEventListener('animationend', handle);
        },
        { once: true },
      );
    }
  }

  function openDetail(trigger) {
    var views = getViews(trigger);
    if (!views) return;
    var targetId = trigger.getAttribute('data-size-guide-open');
    var page = targetId && document.getElementById(targetId);
    if (!page) return;

    var root = views.querySelector(ROOT_SELECTOR);
    if (root) root.hidden = true;
    page.hidden = false;

    // Remember the row so focus returns to it when the page is dismissed.
    views._osgdReturnFocus = trigger;

    resetScroll(views);
    var back = page.querySelector('[data-size-guide-back]');
    if (back) back.focus();
  }

  function closeDetail(back) {
    var views = getViews(back);
    if (!views) return;

    showRoot(views, true);
    resetScroll(views);

    var trigger = views._osgdReturnFocus;
    if (trigger && typeof trigger.focus === 'function') trigger.focus();
    views._osgdReturnFocus = null;
  }

  // Reset a hosted video back to its thumbnail state: poster shown, controls
  // hidden, play overlay restored.
  function resetVideo(container, video) {
    container.classList.remove('is-playing');
    if (!video) return;
    video.controls = false;
    if (typeof video.pause === 'function') video.pause();
    // load() restores the poster frame (currentTime = 0 would show frame 0).
    if (typeof video.load === 'function') video.load();
  }

  function playVideo(trigger) {
    var container = trigger.closest('[data-size-guide-video]');
    if (!container) return;
    var video = container.querySelector('.oddit-size-guide-drawer__video-player');
    if (!video) return;
    container.classList.add('is-playing');
    video.controls = true;
    var played = video.play();
    if (played && typeof played.catch === 'function') played.catch(function () {});
  }

  // Restore the thumbnail + play button once a video finishes (guard rebinding).
  function bindVideos(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll('.oddit-size-guide-drawer__video-player')
      .forEach(function (video) {
        if (video.dataset.osgdVideoBound === 'true') return;
        video.dataset.osgdVideoBound = 'true';
        video.addEventListener('ended', function () {
          var container = video.closest('[data-size-guide-video]');
          if (container) resetVideo(container, video);
        });
      });
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest && e.target.closest('[data-size-guide-open]');
    if (opener) {
      openDetail(opener);
      return;
    }
    var back = e.target.closest && e.target.closest('[data-size-guide-back]');
    if (back) {
      closeDetail(back);
      return;
    }
    var play = e.target.closest && e.target.closest('[data-size-guide-video-play]');
    if (play) {
      playVideo(play);
      return;
    }
    var unitBtn = e.target.closest && e.target.closest('[data-osgd-unit-btn]');
    if (unitBtn) {
      setUnit(unitBtn.closest('.oddit-size-guide-drawer'), unitBtn.getAttribute('data-osgd-unit-btn'), true);
    }
  });

  // Measurement units: the server picks a default by country (in for the US,
  // cm elsewhere); a shopper's own choice is remembered for later visits.
  var UNIT_KEY = 'osgdUnit';

  function setUnit(drawer, unit, remember) {
    if (!drawer || (unit !== 'in' && unit !== 'cm')) return;
    drawer.setAttribute('data-osgd-unit', unit);
    drawer.querySelectorAll('[data-osgd-unit-btn]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-osgd-unit-btn') === unit ? 'true' : 'false');
    });
    if (remember) {
      try {
        localStorage.setItem(UNIT_KEY, unit);
      } catch (err) {}
    }
  }

  function applySavedUnit(root) {
    var saved = null;
    try {
      saved = localStorage.getItem(UNIT_KEY);
    } catch (err) {}
    if (!saved) return;
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.oddit-size-guide-drawer[data-osgd-unit]').forEach(function (drawer) {
      setUnit(drawer, saved, false);
    });
  }

  // Reset to the list whenever the modal closes, so it always reopens on the
  // root view. The toggle event doesn't bubble, so bind per modal (guarded).
  function bindModalReset(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll('.oddit-size-guide-drawer__modal[popover]')
      .forEach(function (modal) {
        if (modal.dataset.osgdResetBound === 'true') return;
        modal.dataset.osgdResetBound = 'true';
        modal.addEventListener('toggle', function (e) {
          if (e.newState === 'open') return;
          modal
            .querySelectorAll('[data-size-guide-views]')
            .forEach(function (views) {
              showRoot(views, false);
              views._osgdReturnFocus = null;
            });
          // Reset any video back to its thumbnail state.
          modal
            .querySelectorAll('[data-size-guide-video]')
            .forEach(function (container) {
              resetVideo(
                container,
                container.querySelector('.oddit-size-guide-drawer__video-player'),
              );
            });
        });
      });
  }

  function init() {
    bindModalReset(document);
    bindVideos(document);
    applySavedUnit(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section without a full page load.
  document.addEventListener('shopify:section:load', function (e) {
    bindModalReset(e.target || document);
    bindVideos(e.target || document);
    applySavedUnit(e.target || document);
  });
})();

/**
 * Calf-fit finder (snippets/oddit-calf-finder.liquid). Compares the shopper's
 * calf with the boot's calf circumference from the same rows the shaft tables
 * show. All maths is in cm; inches are converted on the way in and out.
 * Ease = boot calf minus leg: >= 2.5 cm roomy (jeans tucked in), >= 1 cm good
 * (tights / thin leggings), >= 0 snug (bare leg), below 0 likely too tight.
 */
(function () {
  'use strict';

  var CM_PER_IN = 2.54;

  function parseRows(str) {
    var out = {};
    (str || '').split(/\r?\n/).forEach(function (line) {
      var p = line.split('|').map(function (x) { return x.trim(); });
      if (p.length >= 4 && p[0] && !isNaN(parseFloat(p[3]))) out[p[0]] = parseFloat(p[3]);
    });
    return out;
  }

  function unitOf(finder) {
    var drawer = finder.closest('[data-osgd-unit]');
    return drawer && drawer.getAttribute('data-osgd-unit') === 'in' ? 'in' : 'cm';
  }

  var COMMA = /^fr/i.test(document.documentElement.lang || '');

  function fmt(cm, unit) {
    var n = (unit === 'in' ? cm / CM_PER_IN : cm).toFixed(1);
    if (COMMA) n = n.replace('.', ',');
    return n + (COMMA ? '\u00a0' : ' ') + (unit === 'in' ? 'po' : 'cm').replace('po', COMMA ? 'po' : 'in');
  }

  function verdict(ease) {
    if (ease >= 2.5) return 'roomy';
    if (ease >= 1) return 'good';
    if (ease >= 0) return 'snug';
    return 'tight';
  }

  function sizeSort(a, b) { return parseFloat(a) - parseFloat(b); }

  function setup(finder) {
    if (finder.dataset.cfBound === 'true') return;
    finder.dataset.cfBound = 'true';
    var dataEl = finder.querySelector('[data-cf-data]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var t = data.t;
    var groups = data.groups.map(function (g) {
      return { label: g.label, regular: parseRows(g.regular), wide: parseRows(g.wide) };
    });

    var calfInput = finder.querySelector('[data-cf-calf]');
    var sizeSelect = finder.querySelector('[data-cf-size]');
    var unitEl = finder.querySelector('[data-cf-unit]');
    var result = finder.querySelector('[data-cf-result]');

    var sizes = {};
    groups.forEach(function (g) { Object.keys(g.regular).forEach(function (s) { sizes[s] = true; }); });
    Object.keys(sizes).sort(sizeSort).forEach(function (s) {
      var o = document.createElement('option');
      o.value = s; o.textContent = s;
      sizeSelect.appendChild(o);
    });

    function unitLabel(u) { return u === 'in' && COMMA ? 'po' : u; }
    var lastUnit = unitOf(finder);
    unitEl.textContent = unitLabel(lastUnit);

    function legCm() {
      var v = parseFloat((calfInput.value || '').replace(',', '.'));
      if (isNaN(v) || v <= 0) return null;
      return lastUnit === 'in' ? v * CM_PER_IN : v;
    }

    function line(widthLabel, bootCm, leg, unit) {
      var key = verdict(bootCm - leg);
      return '<li class="oddit-calf-finder__row oddit-calf-finder__row--' + key + '">' +
        '<span class="oddit-calf-finder__width">' + widthLabel + '</span>' +
        '<span class="oddit-calf-finder__verdict">' + t[key] + '</span>' +
        '<span class="oddit-calf-finder__boot">' + t.boot.replace('%s', fmt(bootCm, unit)) + '</span></li>';
    }

    function render() {
      var leg = legCm();
      var size = sizeSelect.value;
      if (leg === null || !size) {
        result.innerHTML = '<p class="oddit-calf-finder__prompt">' + t.prompt + '</p>';
        return;
      }
      var unit = lastUnit;
      var html = '';
      var anyFits = false;
      groups.forEach(function (g) {
        var rows = '';
        if (g.regular[size] != null) {
          rows += line(t.regular, g.regular[size], leg, unit);
          if (g.regular[size] - leg >= 0) anyFits = true;
        }
        var hasWideData = Object.keys(g.wide).length > 0;
        if (hasWideData) {
          if (g.wide[size] != null) {
            rows += line(t.wide, g.wide[size], leg, unit);
            if (g.wide[size] - leg >= 0) anyFits = true;
          } else {
            rows += '<li class="oddit-calf-finder__row oddit-calf-finder__row--na"><span class="oddit-calf-finder__width">' +
              t.wide + '</span><span class="oddit-calf-finder__verdict">' + t.noWideSize + '</span></li>';
          }
        }
        if (!rows) return;
        html += '<div class="oddit-calf-finder__group">';
        if (!data.pdp || groups.length > 1) html += '<p class="oddit-calf-finder__group-label">' + g.label + '</p>';
        html += '<ul class="oddit-calf-finder__rows">' + rows + '</ul>';
        if (data.pdp && !data.hasWide && hasWideData && g.wide[size] != null && g.wide[size] - leg >= 0 &&
            (g.regular[size] == null || g.regular[size] - leg < 1)) {
          html += '<p class="oddit-calf-finder__hint">' + t.wideElsewhere +
            ' <a href="' + data.wideUrl + '">' + t.shopWide + '</a></p>';
        }
        html += '</div>';
      });
      if (!html) {
        result.innerHTML = '<p class="oddit-calf-finder__prompt">' + t.prompt + '</p>';
        return;
      }
      if (!anyFits) {
        html += '<p class="oddit-calf-finder__hint">' + t.allTight +
          ' <a href="' + data.heightUrl + '">' + t.shopHeight + '</a></p>';
      }
      html += '<p class="oddit-calf-finder__note">' + t.note + '</p>';
      result.innerHTML = html;
    }

    // Follow the drawer's in/cm toggle: convert what was typed, then re-render.
    function syncUnit() {
      var u = unitOf(finder);
      if (u === lastUnit) return;
      var v = parseFloat((calfInput.value || '').replace(',', '.'));
      if (!isNaN(v)) {
        var conv = (u === 'in' ? v / CM_PER_IN : v * CM_PER_IN).toFixed(1);
        calfInput.value = COMMA ? conv.replace('.', ',') : conv;
      }
      lastUnit = u;
      unitEl.textContent = unitLabel(u);
      render();
    }
    var drawer = finder.closest('[data-osgd-unit]');
    if (drawer && window.MutationObserver) {
      new MutationObserver(syncUnit).observe(drawer, { attributes: true, attributeFilter: ['data-osgd-unit'] });
    }

    // On a product page, start from the size already picked on the page.
    finder._cfPrefill = function () {
      if (sizeSelect.value) return;
      var active = document.querySelector('.oddit-section-main-product__size-option--active[data-variant-option-value]');
      if (active && sizes[active.getAttribute('data-variant-option-value')]) {
        sizeSelect.value = active.getAttribute('data-variant-option-value');
        render();
      }
    };
    finder._cfPrefill();

    calfInput.addEventListener('input', render);
    sizeSelect.addEventListener('change', render);
    render();
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-calf-finder]').forEach(setup);
  }

  // The drawer opens after the shopper may have picked a size: prefill then.
  document.addEventListener('click', function (e) {
    var opener = e.target.closest && e.target.closest('[data-size-guide-open]');
    if (!opener) return;
    document.querySelectorAll('[data-calf-finder]').forEach(function (f) {
      if (f._cfPrefill) f._cfPrefill();
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();

/**
 * Calf check (snippets/oddit-calf-check.liquid): the on-page slider version of the calf finder. Same rows,
 * same verdicts (ease = boot calf minus leg: >= 2.5 cm roomy, >= 1 good, >= 0 snug, below 0 too tight).
 * The picture is a top-down slice through the shaft: rings for the boot (Regular solid, Wide dashed), a disc for
 * the leg, and any part of the leg outside the Regular ring drawn as a red pinch. Radii are scaled for
 * legibility (1 cm = 4 px), not to true size. Units follow the drawer's saved choice (localStorage osgdUnit).
 */
(function () {
  'use strict';

  var CM_PER_IN = 2.54;
  var UNIT_KEY = 'osgdUnit';
  var COMMA = /^fr/i.test(document.documentElement.lang || '');

  function parseRows(str) {
    var out = {};
    (str || '').split(/\r?\n/).forEach(function (line) {
      var p = line.split('|').map(function (x) { return x.trim(); });
      if (p.length >= 4 && p[0] && !isNaN(parseFloat(p[3]))) out[p[0]] = parseFloat(p[3]);
    });
    return out;
  }

  function verdict(ease) {
    if (ease >= 2.5) return 'roomy';
    if (ease >= 1) return 'good';
    if (ease >= 0) return 'snug';
    return 'tight';
  }

  function radius(cm) { return 30 + (cm - 28) * 4; }

  function setup(root) {
    if (root.dataset.ccBound === 'true') return;
    root.dataset.ccBound = 'true';
    var data;
    try { data = JSON.parse(root.querySelector('[data-cc-data]').textContent); } catch (e) { return; }
    var t = data.t;
    var reg = parseRows(data.regular);
    var wide = parseRows(data.wide);
    var hasWideData = Object.keys(wide).length > 0;
    var showWideRing = data.hasWide && hasWideData;

    var range = root.querySelector('[data-cc-range]');
    var valueEl = root.querySelector('[data-cc-value]');
    var sizeSel = root.querySelector('[data-cc-size]');
    var result = root.querySelector('[data-cc-result]');
    var svg = root.querySelector('svg');
    var ring = root.querySelector('[data-cc-ring]');
    var ringLine = root.querySelector('[data-cc-ring-line]');
    var ringWide = root.querySelector('[data-cc-ring-wide]');
    var leg = root.querySelector('[data-cc-leg]');
    var pinch = root.querySelector('[data-cc-pinch]');
    var maskCircle = root.querySelector('[data-cc-mask]');
    var unitBtns = root.querySelectorAll('[data-cc-unit]');
    var legendWide = root.querySelector('[data-cc-legend-wide]');
    if (!showWideRing) {
      ringWide.style.display = 'none';
      if (legendWide) legendWide.hidden = true;
    }

    var sizes = Object.keys(reg).sort(function (a, b) { return parseFloat(a) - parseFloat(b); });
    sizes.forEach(function (s) {
      var o = document.createElement('option');
      o.value = s;
      o.textContent = s;
      sizeSel.appendChild(o);
    });

    var unit = root.getAttribute('data-unit') === 'in' ? 'in' : 'cm';
    try {
      var saved = localStorage.getItem(UNIT_KEY);
      if (saved === 'in' || saved === 'cm') unit = saved;
    } catch (e) {}
    var legCm = 37;
    var touched = false;
    var sizePicked = false;

    function num(n, d) {
      var s = n.toFixed(d);
      return COMMA ? s.replace('.', ',') : s;
    }
    function show(cm) {
      var sep = COMMA ? ' ' : ' ';
      return unit === 'in' ? num(cm / CM_PER_IN, 1) + sep + t.unitIn : num(cm, 1) + sep + t.unitCm;
    }

    function applyUnit() {
      unitBtns.forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-cc-unit') === unit ? 'true' : 'false'); });
      if (unit === 'in') {
        range.min = '11';
        range.max = '19.75';
        range.step = '0.25';
        range.value = String(Math.round((legCm / CM_PER_IN) * 4) / 4);
      } else {
        range.min = '28';
        range.max = '50';
        range.step = '0.5';
        range.value = String(Math.round(legCm * 2) / 2);
      }
    }

    function row(label, bootCm) {
      var key = verdict(bootCm - legCm);
      return '<li class="oddit-calf-check__row oddit-calf-check__row--' + key + '">' +
        '<span class="oddit-calf-check__width">' + label + '</span>' +
        '<span class="oddit-calf-check__verdict">' + t[key] + '</span>' +
        '<span class="oddit-calf-check__boot">' + t.boot.replace('%s', show(bootCm)) + '</span></li>';
    }

    function render() {
      valueEl.textContent = show(legCm);
      var size = sizeSel.value;
      var r = reg[size];
      var w = wide[size];
      // Picture
      var legR = radius(legCm);
      leg.setAttribute('r', legR.toFixed(1));
      pinch.setAttribute('r', legR.toFixed(1));
      if (r != null) {
        ring.setAttribute('r', radius(r).toFixed(1));
        ringLine.setAttribute('r', radius(r).toFixed(1));
        maskCircle.setAttribute('r', radius(r).toFixed(1));
      }
      if (showWideRing) {
        ringWide.style.display = w != null ? '' : 'none';
        if (w != null) ringWide.setAttribute('r', radius(w).toFixed(1));
      }
      svg.setAttribute('data-verdict', r != null ? verdict(r - legCm) : '');
      root.classList.toggle('is-idle', !touched);
      // Words
      if (!touched || r == null) {
        result.innerHTML = '<p class="oddit-calf-check__prompt">' + t.prompt + '</p>';
        return;
      }
      var rows = row(t.regular, r);
      if (showWideRing) {
        rows += w != null ? row(t.wide, w)
          : '<li class="oddit-calf-check__row oddit-calf-check__row--na"><span class="oddit-calf-check__width">' + t.wide +
            '</span><span class="oddit-calf-check__verdict">' + t.noWideSize + '</span></li>';
      }
      var html = '<ul class="oddit-calf-check__rows">' + rows + '</ul>';
      var fits = r - legCm >= 0 || (showWideRing && w != null && w - legCm >= 0);
      if (!data.hasWide && hasWideData && w != null && w - legCm >= 0 && r - legCm < 1) {
        html += '<p class="oddit-calf-check__hint">' + t.wideElsewhere + ' <a href="' + data.wideUrl + '">' + t.shopWide + '</a></p>';
      } else if (!fits) {
        html += '<p class="oddit-calf-check__hint">' + t.allTight + ' <a href="' + data.heightUrl + '">' + t.shopHeight + '</a></p>';
      }
      result.innerHTML = html;
    }

    // Start on the size picked on the page, else the middle of the run.
    function pageSize() {
      var active = document.querySelector('.oddit-section-main-product__size-option--active[data-variant-option-value]');
      return active && reg[active.getAttribute('data-variant-option-value')] != null ? active.getAttribute('data-variant-option-value') : null;
    }
    sizeSel.value = pageSize() || sizes[Math.floor(sizes.length / 2)] || '';
    document.addEventListener('click', function (e) {
      if (sizePicked || !e.target.closest || !e.target.closest('.oddit-section-main-product__size-option')) return;
      setTimeout(function () {
        var s = pageSize();
        if (s) { sizeSel.value = s; render(); }
      }, 0);
    });

    range.addEventListener('input', function () {
      var v = parseFloat(range.value);
      legCm = unit === 'in' ? v * CM_PER_IN : v;
      touched = true;
      render();
    });
    sizeSel.addEventListener('change', function () { sizePicked = true; render(); });
    unitBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        unit = b.getAttribute('data-cc-unit');
        try { localStorage.setItem(UNIT_KEY, unit); } catch (e) {}
        applyUnit();
        render();
      });
    });

    applyUnit();
    render();
  }

  function init(scope) {
    (scope && scope.querySelectorAll ? scope : document).querySelectorAll('[data-calf-check]').forEach(setup);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
